import { Ban, CheckCircle2, Target } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { CompetencyModal } from '../components/CompetencyModal';
import { competenciesApi } from '../competencyApi';
import type { Competency } from '../competencyTypes';

type TabKey = 'info';

/**
 * One competency in the framework.
 *
 * On the **header** layout rather than the sidebar one: a catalog row reads as a single
 * continuous thing — what the competency covers, where it sits, and the trail of who changed
 * the wording — not a record whose content splits into panels a reader chooses between.
 *
 * It earns its keep on the count: how many L&D records already name a competency is what a
 * reader wants before rewording it or retiring it.
 */
export function CompetencyProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [competency, setCompetency] = useState<Competency | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      competenciesApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) setCompetency(next);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError ? caught : new ApiError('Could not load this competency.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  // A plain string is a segment that goes nowhere; the two links are the way back.
  const breadcrumb: BreadcrumbSegment[] = [
    'Human Resources',
    'Personnel',
    { label: 'Settings', to: '/personnel/settings' },
    { label: 'Competencies', to: '/personnel/settings/competencies' },
    competency ? competency.name : 'Competency',
  ];

  if (error) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => load()}
        />
      </div>
    );
  }

  if (loading || !competency) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="competency-profile"
        header={
          <ProfileHeader
            id="competency-profile-header"
            icon={<Target className="h-6 w-6" aria-hidden="true" />}
            title={competency.name}
            meta={[
              // Unlabelled, because "12 L&D records" says what it is on its own.
              `${competency.trainingCount} L&D ${competency.trainingCount === 1 ? 'record' : 'records'}`,
            ]}
            secondary={competency.category ? [competency.category] : undefined}
            status={
              <StatusText tone={competency.isActive ? 'active' : 'inactive'}>
                {competency.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={competency.createdByName}
            createdAt={competency.createdAt}
            /*
              The record-scoped history, in the header cog and scoped with `entityId` — where
              CLAUDE.md puts it for a profile. Its key is the audit module's, not this one's:
              a control carries the key of what it opens.
            */
            settings={{
              onEdit: () => setEditing(true),
              editPermission: 'competencies.update',
              onHistory: () => setHistoryOpen(true),
            }}
          />
        }
        banner={
          competency.isActive ? (
            <ProfileBanner
              id="competency-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This competency is offered: it appears on the Training/L&D form of every personnel record."
            />
          ) : (
            <ProfileBanner
              id="competency-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This competency is deleted. It is no longer offered, and the records already naming it keep it."
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="competency-details"
          title="Details"
          menuItems={[
            {
              key: 'edit',
              label: 'Edit',
              permission: 'competencies.update',
              onClick: () => setEditing(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Competency', value: competency.name },
              { label: 'Group', value: competency.category },
              { label: 'Description', value: competency.description },
              {
                label: 'Order',
                value: <span className="font-mono tabular-nums">{competency.sortOrder}</span>,
              },
              {
                label: 'L&D records',
                value: <span className="font-mono tabular-nums">{competency.trainingCount}</span>,
              },
            ]}
          />
        </ProfileCard>

        {/* Audit is never editable: created and updated are a record of what happened. */}
        <ProfileCard id="competency-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: competency.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(competency.createdAt)} className="font-mono">
                    {formatDateTime(competency.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: competency.updatedByName },
              {
                label: 'Updated',
                value: competency.updatedAt ? (
                  <time dateTime={toIso(competency.updatedAt)} className="font-mono">
                    {formatDateTime(competency.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <CompetencyModal
          open
          editing={competency}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="competencies"
        entityTypes={['Competency']}
        entityId={competency.id}
      />
    </div>
  );
}
